/* eslint-disable no-param-reassign */
// no-param-reassign: the clips are <video> nodes the effect drives directly (poster, src, muted), as in the design.
import { Trans, useTranslation } from 'next-i18next';
import type { MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { CARD_GRADIENT, CONTAINER, CTA_UTILS, EYEBROW, EYEBROW_MARK, H2, LEAD, MOTION_TOGGLE, SECTION_HEAD, SECTION_PAD } from '../classes';
import { FORM_ID, m, prefersReducedMotion, VIDEO } from '../shared';

type ClipName = 'off' | 'on';
const CLIPS: ClipName[] = ['off', 'on'];
const SMALL_QUERY = '(max-width: 47.99em)';

/** The clip for this viewport (640 under 48em, 1280 above; WebM when the browser plays it, else MP4). */
function pickSource(video: HTMLVideoElement, name: ClipName): string | null {
  const small = typeof window.matchMedia === 'function' && window.matchMedia(SMALL_QUERY).matches;
  const width = small ? 640 : 1280;
  if (video.canPlayType?.('video/webm')) return `${VIDEO}/prova-${name}-${width}.webm`;
  if (video.canPlayType?.('video/mp4')) return `${VIDEO}/prova-${name}-${width}.mp4`;
  return null;
}

export function PlayPauseIcons() {
  return (
    <>
      <svg className='flex-none group-data-[paused=true]:hidden' viewBox='0 0 24 24' width='15' height='15' fill='none' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' aria-hidden='true' focusable='false'>
        <path d='M8.5 5.5v13M15.5 5.5v13' />
      </svg>
      <svg className='hidden flex-none group-data-[paused=true]:block' viewBox='0 0 24 24' width='15' height='15' fill='currentColor' aria-hidden='true' focusable='false'>
        <path d='M8 5.2v13.6a.8.8 0 0 0 1.2.7l11-6.8a.8.8 0 0 0 0-1.4l-11-6.8a.8.8 0 0 0-1.2.7z' />
      </svg>
    </>
  );
}

/**
 * NoPing OFF x ON clips. Poster and clip load only ~250px from the screen, the clip downloaded once as a whole
 * file (fetch → blob, so pausing never aborts ranged requests); they play only while the section is on screen,
 * the tab is visible and the visitor has not paused them. A failed clip keeps its poster (the stills ARE the
 * comparison); with both failed the toggle retires. Reduced motion starts paused.
 */
export function ProofClips({ onTrialClick }: { onTrialClick: (event: MouseEvent<HTMLAnchorElement>) => void }) {
  const { t } = useTranslation('pages/trial-matrix');
  const groupRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);
  const [userPaused, setUserPaused] = useState(false);
  const [allFailed, setAllFailed] = useState(false);
  const syncRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (prefersReducedMotion()) setUserPaused(true);
  }, []);

  useEffect(() => {
    const group = groupRef.current;
    const videos = videoRefs.current.filter((video): video is HTMLVideoElement => !!video);
    if (!group || !videos.length) return undefined;
    const armed = new Set<HTMLVideoElement>();
    const failed = new Set<HTMLVideoElement>();
    const blobUrls: string[] = [];
    let inView = false;
    let disposed = false;

    const markFailed = (video: HTMLVideoElement) => {
      if (failed.has(video)) return;
      failed.add(video);
      if (failed.size >= videos.length) setAllFailed(true);
    };

    const arm = (video: HTMLVideoElement, name: ClipName) => {
      if (armed.has(video)) return;
      armed.add(video);
      video.poster = `${VIDEO}/prova-${name}-poster.webp`;
      const url = pickSource(video, name);
      if (!url) return;
      if (typeof fetch !== 'function' || typeof URL.createObjectURL !== 'function') {
        video.src = url;
        return;
      }
      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.blob();
        })
        .then((blob) => {
          if (disposed) return;
          const blobUrl = URL.createObjectURL(blob);
          blobUrls.push(blobUrl);
          video.src = blobUrl;
          syncRef.current();
        })
        .catch(() => markFailed(video));
    };

    const onError = (event: Event) => markFailed(event.currentTarget as HTMLVideoElement);
    videos.forEach((video) => {
      video.muted = true;
      video.addEventListener('error', onError);
    });

    const sync = () => {
      const shouldPlay = inView && !document.hidden && !(group.dataset.paused === 'true');
      videos.forEach((video, index) => {
        if (failed.has(video)) return;
        if (shouldPlay) {
          arm(video, CLIPS[index] as ClipName);
          if (!video.getAttribute('src')) return;
          video.play()?.catch(() => undefined);
        } else video.pause();
      });
    };
    syncRef.current = sync;

    const observers: IntersectionObserver[] = [];
    if ('IntersectionObserver' in window) {
      const near = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            videos.forEach((video, index) => arm(video, CLIPS[index] as ClipName));
            near.disconnect();
          }
        },
        { rootMargin: '250px 0px' }
      );
      near.observe(group);
      const seen = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            inView = entry.isIntersecting;
          });
          sync();
        },
        { rootMargin: '160px 0px', threshold: 0 }
      );
      seen.observe(group);
      observers.push(near, seen);
    } else {
      inView = true;
      videos.forEach((video, index) => arm(video, CLIPS[index] as ClipName));
    }

    const onVisibility = () => {
      if (document.hidden) videos.forEach((video) => video.pause());
      else sync();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      disposed = true;
      observers.forEach((observer) => observer.disconnect());
      document.removeEventListener('visibilitychange', onVisibility);
      videos.forEach((video) => {
        video.removeEventListener('error', onError);
        video.pause();
      });
      blobUrls.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
      syncRef.current = () => undefined;
    };
  }, []);

  useEffect(() => {
    syncRef.current();
  }, [userPaused]);

  return (
    <section className={`${SECTION_PAD} bg-[linear-gradient(180deg,#070A08,#0B110D_55%,#070A08)]`} id='prova' aria-labelledby='prova-t'>
      <div className={CONTAINER}>
        <div className={SECTION_HEAD} data-reveal=''>
          <p className={EYEBROW}>
            <span className={EYEBROW_MARK} aria-hidden='true'>
              &gt;
            </span>{' '}
            {t('lp.proof.eyebrow')}
          </p>
          <h2 className={H2} id='prova-t'>
            <Trans t={t} i18nKey='lp.proof.title' components={{ em: <em /> }} />
          </h2>
          <p className={LEAD}>{t('lp.proof.lead')}</p>
        </div>

        <div ref={groupRef} className='grid gap-[clamp(1rem,.5rem+1.6vw,1.75rem)] min-[736px]:grid-cols-2' id='prova-clipes' data-paused={userPaused ? 'true' : 'false'}>
          {CLIPS.map((name, index) => {
            const isOn = name === 'on';
            return (
              <figure key={name} className={`isolate m-0 mx-auto w-full max-w-[40rem] overflow-hidden rounded-3xl border ${CARD_GRADIENT} min-[736px]:max-w-none forced-colors:border-[CanvasText] ${isOn ? 'border-[#93FF18]/40 shadow-[0_24px_60px_rgb(0_0_0/.55),0_8px_20px_rgb(0_0_0/.35),0_0_60px_-28px_rgb(147_255_24/.8)]' : 'border-[#FF5230]/[.32] shadow-[var(--shadow-lg)]'}`} data-reveal=''>
                <div className='relative aspect-video overflow-hidden bg-[#050706]'>
                  <video
                    ref={(element) => {
                      videoRefs.current[index] = element;
                    }}
                    className='block size-full object-cover'
                    width={1280}
                    height={720}
                    muted
                    loop
                    playsInline
                    preload='none'
                    disablePictureInPicture
                    aria-label={t(isOn ? 'lp.proof.onAria' : 'lp.proof.offAria')}
                  />
                  <span className='pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(180deg,rgb(0_0_0/.16)_0_1px,transparent_1px_3px)] opacity-[.55] forced-colors:hidden' aria-hidden='true' />
                  <span className={`absolute start-3 top-3 z-[2] inline-flex items-center gap-[.4rem] rounded-lg border bg-[rgb(5_8_6/.82)] px-[.65rem] py-[.3rem] font-mono text-[.6875rem] font-bold uppercase tracking-[.12em] backdrop-blur-[6px] ${isOn ? 'border-[#93FF18]/50 text-[#93FF18]' : 'border-[#FF5230]/[.45] text-[#FF8C70]'}`}>
                    <i className={`size-1.5 rounded-full forced-colors:bg-[Highlight] ${isOn ? 'bg-[#93FF18] shadow-[0_0_8px_rgb(147_255_24/.9)]' : 'bg-[#FF5230] shadow-[0_0_8px_rgb(255_82_48/.9)]'}`} aria-hidden='true' />
                    {t(isOn ? 'lp.proof.onBadge' : 'lp.proof.offBadge')}
                  </span>
                </div>
                <figcaption className='px-5 pb-6 pt-5 sm:p-6'>
                  <strong className={`block text-[length:var(--fs-xl)] font-bold tracking-[-.02em] ${isOn ? 'text-[#93FF18]' : 'text-[#FF8C70]'}`}>{t(isOn ? 'lp.proof.onTitle' : 'lp.proof.offTitle')}</strong> <span className='mt-2 block text-[.875rem] leading-[1.55] text-[#9AA3B5] max-sm:text-[.9375rem]'>{t(isOn ? 'lp.proof.onText' : 'lp.proof.offText')}</span>
                </figcaption>
              </figure>
            );
          })}
        </div>

        <div className='mt-[clamp(1.5rem,1rem+1.6vw,2.25rem)] flex flex-wrap items-center justify-between gap-4'>
          {!allFailed && (
            <button className={MOTION_TOGGLE} type='button' data-paused={userPaused ? 'true' : 'false'} aria-controls='prova-clipes' onClick={() => setUserPaused((paused) => !paused)}>
              <PlayPauseIcons />
              <span>{t(userPaused ? 'lp.proof.play' : 'lp.proof.pause')}</span>
            </button>
          )}
          <a className={`${m('btn-primary cta')} ${CTA_UTILS}`} href={`#${FORM_ID}`} onClick={onTrialClick}>
            {t('lp.cta')}
          </a>
        </div>
      </div>
    </section>
  );
}
